import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight, Newspaper, Tag, Lock, User, Briefcase, ChevronDown, Calendar, LogIn } from "lucide-react";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useAuthModals } from "@/components/auth/AuthModalsHost";
import UserMenu from "@/components/auth/UserMenu";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const areaRef = useRef(null);
  const location = useLocation();
  const isQuestionnaire = location.pathname.startsWith("/questionario");
  const { account } = useClientAuth();
  const { openLogin } = useAuthModals();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (areaRef.current && !areaRef.current.contains(e.target)) setAreaOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") { window.location.href = `/#${id}`; return; }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed left-0 right-0 w-full z-50 transition-all duration-500 ${scrolled ? "bg-white/80 backdrop-blur-2xl shadow-[0_1px_0_rgba(0,0,0,0.05)]" : "bg-transparent"}`}
      style={{ top: "var(--colucci-banner-h, 0px)" }}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
          <img
            src="/logo_bianco.png"
            alt="Colucci Assicuratori"
            width={1840}
            height={470}
            className="block h-8 sm:h-9 w-auto object-contain invert"
          />
        </Link>

        {!isQuestionnaire && (
          <nav className="hidden lg:flex items-center gap-1">
            {[
              { label: "Prodotti", id: "prodotti" },
              { label: "Come Funziona", id: "processo" },
              { label: "Chi Siamo", id: "chi-siamo" },
              { label: "Team", id: "team" },
              { label: "FAQ", id: "faq" },
            ].map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)}
                className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 rounded-full hover:bg-zinc-100/80 transition-all duration-200" data-testid={`nav-${item.id}`}>
                {item.label}
              </button>
            ))}
            <a href="tel:0817611000" className="flex items-center gap-1.5 px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 rounded-full hover:bg-zinc-100/80 transition-all" data-testid="nav-phone">
              <Phone className="w-3.5 h-3.5" /> 081 761 1000
            </a>
            <Link to="/notizie-assicurative" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#163321] hover:text-[#163321] rounded-full bg-[#163321]/5 hover:bg-[#163321]/10 transition-all border border-[#163321]/10" data-testid="nav-notizie">
              <Newspaper className="w-3.5 h-3.5" /> Notizie
            </Link>
            <Link to="/eventi" className="flex items-center gap-1.5 px-4 py-2 text-sm text-amber-700 hover:text-amber-700 rounded-full bg-amber-50 hover:bg-amber-100 transition-all border border-amber-200" data-testid="nav-eventi">
              <Calendar className="w-3.5 h-3.5" /> Eventi
            </Link>
            <Link to="/convenzioni" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[#D97B58] hover:text-[#D97B58] rounded-full bg-[#D97B58]/8 hover:bg-[#D97B58]/15 transition-all border border-[#D97B58]/30" data-testid="nav-convenzioni">
              <Tag className="w-3.5 h-3.5" /> Convenzioni
            </Link>
            {/* FASE 4.2 — Area Cliente UNIFICATA: bottone "Accedi" o UserMenu */}
            {account ? (
              <UserMenu />
            ) : (
              <button
                onClick={openLogin}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900 rounded-full hover:bg-zinc-100/80 transition-all border border-zinc-200"
                data-testid="header-login-btn"
              >
                <LogIn className="w-3.5 h-3.5" /> Accedi
              </button>
            )}
            {/* Staff dropdown rimane per accesso operatori */}
            <div ref={areaRef} className="relative">
              <button
                onClick={() => setAreaOpen(!areaOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700 rounded-full hover:bg-zinc-100/60 transition-all"
                data-testid="nav-area-staff-trigger"
                title="Area Staff (operatori Colucci)"
              >
                <Lock className="w-3.5 h-3.5" />
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${areaOpen ? "rotate-180" : ""}`} />
              </button>
              {areaOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-zinc-100 overflow-hidden z-[60]"
                  data-testid="area-staff-dropdown"
                >
                  <Link
                    to="/admin"
                    onClick={() => setAreaOpen(false)}
                    title="Riservato a operatori autorizzati"
                    className="flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors"
                    data-testid="nav-area-staff"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <Briefcase className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#163321]">Area Staff</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Operatori Colucci</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            <Link to="/consulenza" className="ml-2 inline-flex items-center gap-2 px-6 h-10 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-lg shadow-[#163321]/20" data-testid="header-cta-btn">
              Preventivo Gratuito <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </nav>
        )}

        <button className="lg:hidden p-2 rounded-full hover:bg-zinc-100" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5 text-zinc-900" /> : <Menu className="w-5 h-5 text-zinc-900" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-2xl border-t border-zinc-100 px-6 py-6 space-y-1 animate-fade-in">
          {["Prodotti", "Come Funziona", "Chi Siamo", "Team", "FAQ"].map(label => (
            <button key={label} onClick={() => scrollTo(label.toLowerCase().replace(/\s/g, "-"))}
              className="block w-full text-left px-4 py-3 text-sm text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 rounded-xl transition-colors">
              {label}
            </button>
          ))}
          <a href="tel:0817611000" className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-600">
            <Phone className="w-4 h-4" /> 081 761 1000
          </a>
          <Link to="/notizie-assicurative" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-[#163321] font-medium" data-testid="mobile-nav-notizie">
            <Newspaper className="w-4 h-4" /> Notizie Assicurative
          </Link>
          <Link to="/eventi" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-amber-700 font-medium" data-testid="mobile-nav-eventi">
            <Calendar className="w-4 h-4" /> Eventi
          </Link>
          <Link to="/convenzioni" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-[#D97B58] font-medium" data-testid="mobile-nav-convenzioni">
            <Tag className="w-4 h-4" /> Convenzioni Aziendali
          </Link>
          <div className="border-t border-zinc-100 mt-2 pt-2">
            <p className="px-4 py-1 text-[10px] uppercase tracking-wide text-zinc-400 font-bold">Area Riservata</p>
            {account ? (
              <>
                <p className="px-4 py-2 text-xs text-emerald-700 font-medium">Ciao {account.email?.split("@")[0]}</p>
                <button onClick={() => { setMobileOpen(false); openLogin(); /* mobile fallback opens login menu */ }}
                  className="w-full flex items-start gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-emerald-50 rounded-xl text-left"
                  data-testid="mobile-nav-user">
                  <User className="w-4 h-4 mt-0.5 text-emerald-700" />
                  <div>
                    <span className="font-medium block">Area Cliente</span>
                    <span className="text-[11px] text-zinc-500">Dashboard, polizze, documenti</span>
                  </div>
                </button>
              </>
            ) : (
              <button onClick={() => { setMobileOpen(false); openLogin(); }}
                className="w-full flex items-start gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-emerald-50 rounded-xl text-left"
                data-testid="mobile-nav-login">
                <LogIn className="w-4 h-4 mt-0.5 text-emerald-700" />
                <div>
                  <span className="font-medium block">Accedi</span>
                  <span className="text-[11px] text-zinc-500">Area Clienti Colucci</span>
                </div>
              </button>
            )}
            <Link to="/admin" onClick={() => setMobileOpen(false)}
                   className="flex items-start gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-slate-50 rounded-xl"
                   data-testid="mobile-nav-area-staff">
              <Briefcase className="w-4 h-4 mt-0.5 text-slate-600" />
              <div>
                <span className="font-medium block">Area Staff</span>
                <span className="text-[11px] text-zinc-500">Operatori Colucci</span>
              </div>
            </Link>
          </div>
          <Link to="/consulenza" onClick={() => setMobileOpen(false)}
            className="block w-full text-center mt-3 px-6 py-3 bg-[#163321] text-white text-sm font-semibold rounded-full" data-testid="mobile-cta-btn">
            Preventivo Gratuito
          </Link>
        </div>
      )}
    </header>
  );
}
